import { useTranslation } from 'react-i18next'
import { formatDate } from './cycleStatus'

const round = (value) => (value === null || value === undefined ? null : Math.round(Number(value)))

const TARGET_OF = { kcal: 'kcal', protein_g: 'protein_g', carbs_g: 'carbs_rest_day_g' }

/**
 * VH-236 follow-up: the week a menu change lands in, spelled out.
 *
 * A change put into a week the user is not reading looks to them like nothing happened at all —
 * which is exactly what went wrong in production (cycle 1, 2026-09-16).
 */
export function MenuWeek({ week, slot, dayTypes, testId }) {
  const { t, i18n } = useTranslation()
  if (!week?.week_start) return null
  return (
    <span className="block font-medium text-gray-900" data-testid={testId}>
      {t(slot ? 'healthCycle.changeSet.addMenuItem.weekDetail' : 'healthCycle.changeSet.addMenuItem.week', {
        start: formatDate(week.week_start, i18n.language),
        end: formatDate(week.week_end, i18n.language),
        slot: slot ? t(`mealPrep.slots.${slot}`, slot) : '',
        days: dayTypes ?? '',
      })}
    </span>
  )
}

/**
 * VH-236: the preview of "put this food or recipe into the weekly plan".
 *
 * Shows what one portion adds and, per affected day, the day total before and after against the
 * cycle's targets — the numbers the server computed, so the user confirms a real change, not a claim.
 */
export function MenuItemChange({ operation }) {
  const { t } = useTranslation()
  const after = operation.after ?? {}
  const effect = operation.effect ?? {}
  const days = effect.day_totals ?? {}
  const targets = effect.targets ?? {}
  // VH-236 follow-up: the week is named, never implied — a change put in a week the user is not
  // reading looks like nothing happened at all (production, cycle 1).
  const week = effect.week ?? {}
  const dayTypes = (after.day_types ?? []).map((d) => t(`mealPrep.dayTypes.${d}`, d)).join(' / ')

  return (
    <div className="mt-1 text-xs" data-testid={`menu-item-change-${operation.id}`}>
      <MenuWeek week={week} slot={after.meal_slot} dayTypes={dayTypes} testId={`menu-item-week-${operation.id}`} />
      <p className="text-gray-900">
        {t('healthCycle.changeSet.addMenuItem.summary', {
          name: after.name,
          grams: round(after.quantity_g),
          slot: t(`mealPrep.slots.${after.meal_slot}`, after.meal_slot),
          kcal: round(after.kcal),
        })}
      </p>
      {after.note && <p className="text-gray-600">{after.note}</p>}
      <ul className="mt-1 space-y-0.5">
        {Object.entries(days).map(([dayType, totals]) => (
          <li key={dayType} className="text-gray-700">
            <span className="font-medium block">{t(`mealPrep.dayTypes.${dayType}`, dayType)}</span>
            <span className="flex flex-wrap gap-x-3">
            {['kcal', 'protein_g', 'carbs_g'].map((key) => {
              const before = totals.before?.[key]
              const value = totals.after?.[key]
              if (value === undefined) return null
              const target = targets[TARGET_OF[key]]
              return (
                <span key={key} className="whitespace-nowrap">
                  {t(`recipes.nutrition.nutrient.${key}`)}: <span className="line-through">{round(before)}</span>{' '}
                  → <span className="font-medium">{round(value)}</span>
                  {target ? <span className="text-gray-500"> / {round(target)}</span> : null}
                </span>
              )
            })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default MenuItemChange
